import React, { useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Select, Tag, Tooltip } from "antd";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import moment from "moment";

import PayrollDataTable from "./PayrollDataTable";
import EmployeeAvatar from "./EmployeeAvatar";
import DownloadDetailModal from "./modals/DownloadDetailModal";
import {
  buildDepartmentMap,
  buildEmployeeMap,
} from "../helpers/payrollDisplay.helper";
import {
  downloadHistoricalReport,
  listPayrollReportDownloads,
} from "../../../../../services/attendanceService";
import { notify } from "../../../../../services/toast.config";
import { getErrorMessage } from "../../../../../core/data/redux/utils/errorUtils";

import type { PayrollDownloadHistoryRow } from "../types";

interface PayrollHistoryTabProps {
  selectedCompany: any;
  employees: any[];
  departments: any[];
  refreshKey: number;
  active: boolean;
}

interface HistoryFilters {
  month: Dayjs | null;
  downloadedByUserId: number | null;
  range: [Dayjs | null, Dayjs | null] | null;
}

const EMPTY_FILTERS: HistoryFilters = {
  month: null,
  downloadedByUserId: null,
  range: null,
};

const scopeSummary = (row: PayrollDownloadHistoryRow) => {
  const depts = row.filter_config?.department_ids?.length || 0;
  const emps = row.filter_config?.employee_ids?.length || 0;
  const deptLabel = depts ? `${depts} dept${depts > 1 ? "s" : ""}` : "All depts";
  const empLabel = emps ? `${emps} emp${emps > 1 ? "s" : ""}` : "All emps";
  return `${deptLabel} / ${empLabel}`;
};

export const PayrollHistoryTab: React.FC<PayrollHistoryTabProps> = ({
  selectedCompany,
  employees,
  departments,
  refreshKey,
  active,
}) => {
  const [rows, setRows] = useState<PayrollDownloadHistoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [redownloadingId, setRedownloadingId] = useState<number | null>(null);
  const [detailRecord, setDetailRecord] =
    useState<PayrollDownloadHistoryRow | null>(null);

  const [tempFilters, setTempFilters] = useState<HistoryFilters>(EMPTY_FILTERS);
  const [filters, setFilters] = useState<HistoryFilters>(EMPTY_FILTERS);

  const employeeMap = useMemo(() => buildEmployeeMap(employees), [employees]);
  const departmentMap = useMemo(
    () => buildDepartmentMap(departments),
    [departments],
  );

  const activeFiltersCount =
    (filters.month ? 1 : 0) +
    (filters.downloadedByUserId ? 1 : 0) +
    (filters.range && filters.range[0] ? 1 : 0);

  const employeeOptions = useMemo(
    () =>
      (Array.isArray(employees) ? employees : []).map((emp: any) => ({
        value: Number(emp.id),
        label:
          `${emp.fname || ""} ${emp.lname || ""}`.trim() ||
          emp.username ||
          `Employee-${emp.id}`,
      })),
    [employees],
  );

  const fetchPage = async (
    nextPage: number,
    nextLimit: number,
    f: HistoryFilters,
  ) => {
    if (!selectedCompany?.value) return;
    setLoading(true);
    try {
      const payload: Record<string, any> = {
        c_id: selectedCompany.value,
        c_type: selectedCompany.type,
        page: nextPage,
        per_page: nextLimit,
      };
      if (f.month) payload.month = f.month.format("YYYY-MM");
      if (f.downloadedByUserId)
        payload.downloaded_by_user_id = f.downloadedByUserId;
      if (f.range && f.range[0])
        payload.start_date = f.range[0].format("YYYY-MM-DD");
      if (f.range && f.range[1])
        payload.end_date = f.range[1].format("YYYY-MM-DD");

      const res = await listPayrollReportDownloads(payload);
      const body = res?.data?.data || {};
      setRows(body.data || []);
      setTotal(body.pagination?.total || 0);
      setPage(body.pagination?.current_page || nextPage);
      setLimit(body.pagination?.per_page || nextLimit);
    } catch (err) {
      notify.error(getErrorMessage(err));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!active || !selectedCompany?.value) return;
    fetchPage(1, limit, filters);
  }, [active, selectedCompany?.value, filters, refreshKey]);

  const applyFilters = () => setFilters(tempFilters);

  const resetFilters = () => {
    setTempFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
  };

  const handleRedownload = async (record: PayrollDownloadHistoryRow) => {
    setRedownloadingId(record.id);
    try {
      const res: any = await downloadHistoricalReport({
        id: record.id,
        c_id: selectedCompany?.value,
        c_type: selectedCompany?.type,
      });
      const cd = res?.headers?.["content-disposition"] || "";
      const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
      const filename = match
        ? decodeURIComponent(match[1])
        : record.file_name || `Payroll-Report-${record.month}.xlsx`;

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      notify.error(getErrorMessage(err));
    } finally {
      setRedownloadingId(null);
    }
  };

  const columns: ColumnsType<PayrollDownloadHistoryRow> = [
    {
      title: "When",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (v: string) => (
        <span style={{ fontSize: 13 }}>
          {moment(v).format("DD MMM YY HH:mm")}
        </span>
      ),
    },
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      width: 120,
      render: (v: string) => (
        <Tag color="blue" style={{ margin: 0 }}>
          {v ? moment(v, "YYYY-MM").format("MMM YYYY") : "—"}
        </Tag>
      ),
    },
    {
      title: "Downloaded By",
      dataIndex: "downloaded_by_name",
      key: "downloaded_by_name",
      width: 220,
      render: (v: string | null, record) => {
        const emp = employeeMap.get(Number(record.downloaded_by_user_id));
        const name = v || emp?.name || `User #${record.downloaded_by_user_id}`;
        return (
          <div className="d-flex align-items-center gap-2">
            <EmployeeAvatar name={name} image={emp?.image} size={32} />
            <span style={{ fontSize: 13.5, fontWeight: 500, color: "#0f172a" }}>
              {name}
            </span>
          </div>
        );
      },
    },
    {
      title: "Scope",
      key: "scope",
      width: 170,
      render: (_: any, record) => (
        <span style={{ fontSize: 13, color: "#475569" }}>
          {scopeSummary(record)}
        </span>
      ),
    },
    {
      title: "Exceptions",
      key: "exceptions",
      width: 160,
      render: (_: any, record) => {
        const list = Array.isArray(record.exceptions) ? record.exceptions : [];
        if (!list.length)
          return <span style={{ color: "#94a3b8", fontSize: 13 }}>None</span>;
        const targeted = list.filter(
          (e) => e.applies_to && e.applies_to.type !== "all",
        ).length;
        return (
          <Tag color="purple" style={{ margin: 0 }}>
            {list.length} day{list.length > 1 ? "s" : ""}
            {targeted ? ` · ${targeted} targeted` : ""}
          </Tag>
        );
      },
    },
    {
      title: "Template",
      dataIndex: "template_id",
      key: "template_id",
      width: 100,
      align: "center",
      render: (v: number | null) =>
        v ? (
          <Tag color="geekblue" style={{ margin: 0 }}>
            Template
          </Tag>
        ) : (
          <span style={{ color: "#94a3b8" }}>—</span>
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 170,
      align: "center",
      fixed: "right",
      render: (_: any, record) => (
        <div className="d-inline-flex align-items-center gap-2">
          <Tooltip title="View details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => setDetailRecord(record)}
            />
          </Tooltip>
          {record.status === "too_large" ? (
            <Tooltip title="File was too large to store; re-download unavailable">
              <Button size="small" icon={<DownloadOutlined />} disabled>
                Unavailable
              </Button>
            </Tooltip>
          ) : (
            <Button
              size="small"
              type="primary"
              ghost
              icon={<DownloadOutlined />}
              loading={redownloadingId === record.id}
              onClick={() => handleRedownload(record)}
            >
              Download
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div
        className="mb-3 p-3"
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 10,
        }}
      >
        <div className="d-flex align-items-end justify-content-between flex-wrap gap-2">
          <div className="d-flex gap-3 flex-wrap align-items-end">
            <div style={{ minWidth: 170 }}>
              <div className="text-muted small mb-1">Month</div>
              <DatePicker
                picker="month"
                value={tempFilters.month}
                onChange={(v) =>
                  setTempFilters((p) => ({ ...p, month: v || null }))
                }
                format="MMMM YYYY"
                style={{ width: "100%", minHeight: 38 }}
              />
            </div>
            <div style={{ minWidth: 230 }} className="mb-2">
              <div className="text-muted small mb-1">Downloaded By</div>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                value={tempFilters.downloadedByUserId || undefined}
                onChange={(v) =>
                  setTempFilters((p) => ({
                    ...p,
                    downloadedByUserId: v ?? null,
                  }))
                }
                options={employeeOptions}
                placeholder="Anyone"
                style={{ width: "100%", minHeight: 38 }}
              />
            </div>
            <div style={{ minWidth: 250 }}>
              <div className="text-muted small mb-1">Downloaded Between</div>
              <DatePicker.RangePicker
                value={tempFilters.range as any}
                onChange={(v) =>
                  setTempFilters((p) => ({
                    ...p,
                    range: (v as [Dayjs | null, Dayjs | null]) || null,
                  }))
                }
                format="YYYY-MM-DD"
                style={{ width: "100%", minHeight: 38 }}
              />
            </div>
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className={`btn ${activeFiltersCount > 0 ? "btn-warning" : "btn-outline-danger"
                }`}
              onClick={resetFilters}
              disabled={activeFiltersCount === 0}
              style={{ minHeight: 38 }}
            >
              <i className="ti ti-refresh me-1" />
              Reset
            </button>
            <button
              type="button"
              className="btn btn-info"
              onClick={applyFilters}
              style={{ minHeight: 38 }}
            >
              <i className="ti ti-filter me-1" />
              Apply
            </button>
          </div>
        </div>
      </div>

      <PayrollDataTable<PayrollDownloadHistoryRow>
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        scrollX={1250}
        emptyText="No payroll reports downloaded yet"
        emptyIcon="ti ti-file-download"
        serverPagination={{
          page,
          limit,
          total,
          onPageChange: (p) => fetchPage(p, limit, filters),
          onLimitChange: (l) => fetchPage(1, l, filters),
        }}
      />

      <DownloadDetailModal
        open={Boolean(detailRecord)}
        onClose={() => setDetailRecord(null)}
        record={detailRecord}
        employeeMap={employeeMap}
        departmentMap={departmentMap}
      />
    </div>
  );
};

export default PayrollHistoryTab;
